import React, { createContext, useCallback, useContext, useState } from 'react';
import PermissionPromptModal from '../components/permission-prompt-modal/permission-prompt-modal';
import { openAppSettings } from '../helpers/open-app-settings';

type PermissionType = 'microphone' | 'notifications' | 'storage';

const MESSAGES: Record<PermissionType, { title: string; subtitle: string }> = {
  microphone: {
    title: 'Microphone access is blocked',
    subtitle: 'To record voice messages, open Settings and enable microphone access for Mailander.',
  },
  notifications: {
    title: 'Notifications are blocked',
    subtitle: 'To receive message notifications, open Settings and enable notifications for Mailander.',
  },
  storage: {
    title: 'Storage access is blocked',
    subtitle: 'To download files, open Settings and enable storage access for Mailander.',
  },
};

type PermissionAlertContextValue = {
  showPermissionAlert: (type: PermissionType) => void;
};

const PermissionAlertContext = createContext<PermissionAlertContextValue | null>(null);

/**
 * Fire-and-forget: emit a global event that the provider listens for.
 * Use this from hooks/utils that don't have access to React context.
 */
export function emitPermissionAlert(type: PermissionType): void {
  window.dispatchEvent(new CustomEvent('permission-alert', { detail: type }));
}

export const PermissionAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alertType, setAlertType] = useState<PermissionType | null>(null);

  const showPermissionAlert = useCallback((type: PermissionType) => {
    setAlertType(type);
  }, []);

  // Listen for global events from non-React code
  React.useEffect(() => {
    const handler = (e: Event) => {
      const type = (e as CustomEvent<PermissionType>).detail;
      if (type) setAlertType(type);
    };
    window.addEventListener('permission-alert', handler);
    return () => window.removeEventListener('permission-alert', handler);
  }, []);

  const handlePrimary = useCallback(async () => {
    setAlertType(null);
    await openAppSettings();
  }, []);

  const handleDismiss = useCallback(() => {
    setAlertType(null);
  }, []);

  const msg = alertType ? MESSAGES[alertType] : null;

  return (
    <PermissionAlertContext.Provider value={{ showPermissionAlert }}>
      {children}
      {msg && (
        <PermissionPromptModal
          open={!!alertType}
          onOpenChange={(open) => { if (!open) handleDismiss(); }}
          title={msg.title}
          subtitle={msg.subtitle}
          primaryLabel="Open Settings"
          onPrimary={handlePrimary}
          onSkip={handleDismiss}
        />
      )}
    </PermissionAlertContext.Provider>
  );
};

export function usePermissionAlert() {
  const ctx = useContext(PermissionAlertContext);
  if (!ctx) throw new Error('usePermissionAlert must be used within PermissionAlertProvider');
  return ctx;
}
